import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Form } from 'react-bootstrap';
import Select from 'react-select';

import { API_INTEGRATION_LIST } from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useGet from 'lib/hooks/useGet';

function APIIntegrationSelector({
  globalVendorId = null,
  selectedIntegrationId = null,
  onIntegrationChange,
  label = 'API Integration',
}) {
  // Fetch API integrations for the vendor
  const { data: { data: apiIntegrations = [] } = {}, loading: loadingIntegrations } = useGet(
    API_INTEGRATION_LIST,
    {
      global_vendor_id: globalVendorId,
    },
    !globalVendorId, // skip if no vendor ID
  );

  // Convert API integrations to dropdown options
  const apiIntegrationOptions = useMemo(() => {
    return (apiIntegrations || []).map(integration => ({
      value: integration.id,
      label: `${integration.integration_type} - ${integration.user_name || 'No username'}`,
      data: integration,
    }));
  }, [apiIntegrations]);

  // Find the selected option
  const selectedApiIntegrationOption = useMemo(() => {
    return apiIntegrationOptions.find(opt => opt.value === selectedIntegrationId) || null;
  }, [apiIntegrationOptions, selectedIntegrationId]);

  const handleIntegrationChange = option => {
    if (onIntegrationChange) {
      onIntegrationChange(option ? option.value : null);
    }
  };

  if (loadingIntegrations) {
    return <LoadingSpinner short />;
  }

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <Select
        value={selectedApiIntegrationOption}
        options={apiIntegrationOptions}
        onChange={handleIntegrationChange}
        isClearable
        placeholder={
          apiIntegrationOptions.length === 0
            ? 'No API integrations found for this distributor'
            : 'Select an API integration...'
        }
        isDisabled={!globalVendorId}
      />
    </Form.Group>
  );
}

APIIntegrationSelector.propTypes = {
  globalVendorId: PropTypes.number,
  selectedIntegrationId: PropTypes.number,
  onIntegrationChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default APIIntegrationSelector;
